import constants from '../../constants';
import { FeatureFlag } from './featureFlagTypes';

const API_BASE = constants.baseApiHost;
const API_KEY = constants.apikey;

// Get session from localStorage
const getSession = () => {
  const localStorageSession = localStorage['session'];
  return localStorageSession && localStorageSession !== 'undefined'
    ? JSON.parse(localStorageSession)
    : null;
};

// Common API headers
const getHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    ApiKey: API_KEY || '',
    'Content-Type': 'application/json',
    Authorization: session?.key ? `bearer ${session.key}` : ''
  };
};

// Common API error handling
const handleApiResponse = async (response: Response) => {
  // Error responses may carry an empty or non-JSON body (e.g. some 500s), so
  // tolerate a failed parse there and fall back to the status-based message.
  // A successful response must still be valid JSON: silently treating an
  // unparseable 2xx as an empty flag set would, via the fail-open selector,
  // enable gated features by accident. A 204 legitimately has no body.
  let data: any = null;
  if (response.status !== 204) {
    try {
      data = await response.json();
    } catch {
      if (response.ok) {
        throw new Error('API Error: invalid JSON in successful response');
      }
      data = null;
    }
  }

  if (!response.ok || data?.success === false) {
    const message =
      (typeof data?.message === 'string' && data.message) ||
      (Array.isArray(data?.error) && data.error[0]?.error) ||
      `${response.status} ${response.statusText}`;
    throw new Error(`API Error: ${message}`);
  }

  return data;
};

/**
 * Fetch the application feature flags for the current session.
 * Returns the paginated `{ data: { list: [...] } }` envelope's list, or an empty array.
 */
export const fetchFeatureFlagsApi = async (): Promise<FeatureFlag[]> => {
  const response = await fetch(`${API_BASE}/api/v3/featureflag?keySuffix=UI`, {
    method: 'GET',
    headers: getHeaders()
  });

  const json = await handleApiResponse(response);
  return json?.data?.list ?? [];
};
